import * as io from "io-ts";
import { BlockImport, BlockModel } from ".";
import { Duration, ISODatetime } from "../period";
import { withMessage } from "io-ts-types";
import t from "../../../../../i18n";
import { Encoding } from "../encoding";
import { BlockData } from "./data";

/**
 * @description
 * Available Plot types.
 * Currently correspond to Highcharts series type.
 * @see {https://api.highcharts.com/highstock/series}
 */
export const Mark = withMessage(
  io.keyof({
    line: null,
    scatter: null,
    //   column: null,
  }),
  input => t("schema.layer.mark", `${input}`, ["line", "scatter"].join(", ")),
);
export type MarkT = io.TypeOf<typeof Mark>;

// const StackingOptions = io.keyof({
//   normal: null,
//   overlap: null,
//   percent: null,
//   stream: null,
// });

const Options = io.partial({
  groupYAxesByUnit: io.boolean,
  navigator: io.boolean,
  sync: io.boolean,
  displayCycle: io.boolean,
  displayPublication: io.boolean,
  displayInvalidatedDataPoints: io.boolean,
  temporalZoom: io.union([Duration, io.null]),
  showOvertakingPlotBands: io.union([io.boolean, io.undefined]),
  //   column: io.strict({
  //     /** @see {https://api.highcharts.com/highstock/series.column.stacking} */
  //     stacking: io.union([io.undefined, StackingOptions]),
  //   }),
  //   area: io.strict({
  //     /** @see {https://api.highcharts.com/highstock/series.area.stacking} */
  //     stacking: io.union([io.undefined, StackingOptions]),
  //   }),
});

export type OptionT = io.TypeOf<typeof Options>;

export const BlockPlotKind = withMessage(io.strict({ kind: io.literal("PLOT") }), () =>
  t("schema.block.kind"),
);

export const BlockPlotData = io.type({
  data: BlockData,
});

const Zoom = withMessage(
  io.strict({
    min: io.union([io.null, io.number, ISODatetime]),
    max: io.union([io.null, io.number, ISODatetime]),
  }),
  () => t("schema.layer.zoom"),
);

export type ZoomT = io.TypeOf<typeof Zoom>;

export const RuleLabel = io.keyof({
  min: null,
  max: null,
});
export type RuleLabelT = io.TypeOf<typeof RuleLabel>;
export const Rule = withMessage(
  io.strict({
    value: io.union([io.number, ISODatetime, io.null]),
    label: io.union([RuleLabel, io.null, io.undefined]),
  }),
  () => t("schema.layer.rule"),
);
export type RuleT = io.TypeOf<typeof Rule>;

const Layer = io.type({
  /**
   * @description
   * Mark type.
   * Currently correspond to Highcharts series type.
   *
   * @see {https://api.highcharts.com/highstock/series}
   */
  mark: Mark,
  name: io.union([io.string, io.undefined]),
  encoding: io.intersection([
    io.type({
      x: Encoding,
      y: Encoding,
    }),
    io.partial({
      /**
       * @todo Encoding display
       * Instead of using letters, maybe we should use
       * a more explicit key, like "color" or "size",
       * to specify encoding display.
       */
      z: io.union([Encoding, io.undefined]),
      w: io.union([Encoding, io.undefined]),
      color: io.union([Encoding, io.undefined]),
    }),
  ]),
  zoom: io.union([
    io.partial({
      x: Zoom,
      y: Zoom,
    }),
    io.undefined,
  ]),
  rule: io.union([
    io.partial({
      x: io.array(Rule),
      y: io.array(Rule),
    }),
    io.undefined,
  ]),
  /** @description timeseries axis position */
  position: withMessage(io.union([io.literal("left"), io.literal("right"), io.undefined]), () =>
    t("schema.layer.position"),
  ),
});

export const BlockPlotConfig = io.type({
  config: io.intersection([
    io.type({
      layers: io.array(Layer),
    }),
    io.partial({
      options: Options,
    }),
  ]),
});

export const BlockPlotModel = io.intersection([
  BlockModel,
  BlockPlotKind,
  BlockPlotData,
  BlockPlotConfig,
]);

export const BlockPlotImport = io.intersection([
  BlockImport,
  BlockPlotKind,
  BlockPlotData,
  BlockPlotConfig,
]);

export type BlockPlotModelT = io.TypeOf<typeof BlockPlotModel>;
export type BlockPlotImportT = io.TypeOf<typeof BlockPlotImport>;
